import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { RefreshCw, History, AlertTriangle } from 'lucide-react';

import { parseUtc } from '../../lib/parseUtc';
/**
 * PlanHistoryPanel — compact timeline of the in-cluster engine's
 * RebalancePlan phase history (CAST-AI-style "what did the rebalancer do").
 *
 * Reads GET /v1/clusters/{id}/node-optimization/plan-history — newest-first
 * entries the backend appends whenever a plan changes phase:
 *   { entries: [ {plan_name, phase, mode, nodes_removed, nodes_created,
 *                 projected_monthly_usd, failure_phase, observed_at} ] }
 *
 * Self-contained: owns its own axios call. Quietly renders NOTHING when the
 * endpoint 404s (older backend) or errors, and an inline "no history yet"
 * empty state when the collection is empty. Every field is optional.
 */

// Phase → badge colour. Completed=emerald, Failed=red, everything else
// (Pending/Ready/Executing/…) indigo = in-flight.
const phaseBadge = (phase) => {
  switch (phase) {
    case 'Completed':
      return <Badge className="bg-emerald-100 text-emerald-800">Completed</Badge>;
    case 'Failed':
      return <Badge className="bg-red-100 text-red-800">Failed</Badge>;
    default:
      return <Badge className="bg-indigo-100 text-indigo-800">{phase || 'Unknown'}</Badge>;
  }
};

// Keep the timeline compact — the backend defaults to 50 entries; we show
// the newest 15 and summarise the rest.
const MAX_ENTRIES = 15;

const PlanHistoryPanel = ({ clusterId }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  const fetchHistory = useCallback(async () => {
    if (!clusterId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/v1/clusters/${clusterId}/node-optimization/plan-history`
      );
      setEntries(Array.isArray(res.data?.entries) ? res.data.entries : []);
      setUnavailable(false);
    } catch {
      // Older backend without this endpoint → hide the panel entirely.
      setUnavailable(true);
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  if (unavailable) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-sm">
          <History className="h-4 w-4 text-indigo-600" />
          Plan History
        </CardTitle>
        <button
          onClick={fetchHistory}
          className="text-gray-400 hover:text-gray-600"
          title="Refresh plan history"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </CardHeader>
      <CardContent>
        {loading && entries.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500">Loading plan history…</p>
        ) : entries.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500">
            No plan history yet — entries appear as the engine's rebalance
            plans progress through their phases.
          </p>
        ) : (
          <ul className="space-y-2">
            {entries.slice(0, MAX_ENTRIES).map((e, i) => (
              <li
                key={`${e.plan_name || 'plan'}-${e.phase || ''}-${i}`}
                className="flex items-center gap-2 flex-wrap text-xs border-b last:border-b-0 pb-2 last:pb-0"
              >
                {phaseBadge(e.phase)}
                <span className="font-mono font-medium truncate max-w-[16rem]" title={e.plan_name || ''}>
                  {e.plan_name || '—'}
                </span>
                {e.mode && (
                  <Badge className="bg-gray-100 text-gray-700 font-normal">{e.mode}</Badge>
                )}
                <span className="text-gray-600">
                  −{e.nodes_removed || 0} / +{e.nodes_created || 0} nodes
                </span>
                <span className="text-green-700 font-medium">
                  ${Number(e.projected_monthly_usd || 0).toFixed(2)}/mo
                </span>
                {e.failure_phase && (
                  <Badge
                    className="bg-red-100 text-red-800 flex items-center gap-1"
                    title={`Failed at: ${e.failure_phase}`}
                  >
                    <AlertTriangle className="h-3 w-3" />
                    {e.failure_phase}
                  </Badge>
                )}
                {e.observed_at && (
                  <span className="ml-auto text-gray-400">
                    {parseUtc(e.observed_at).toLocaleString()}
                  </span>
                )}
              </li>
            ))}
            {entries.length > MAX_ENTRIES && (
              <li className="text-xs text-gray-400">
                +{entries.length - MAX_ENTRIES} older entries
              </li>
            )}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default PlanHistoryPanel;
